import { Component, Element, Host, Prop, Watch, h } from '@stencil/core';
import { isMobile, renderResponsiveImages } from '../../helpers/helpers';
import type {
  CssClassMap,
  HTMLCmsModalElement,
  ResponsiveImages,
} from '../../helpers/utils/interfaces';

@Component({
  tag: 'cms-work-steps',
  styleUrl: 'cms-work-steps.scss',
  shadow: true,
})
export class CmsWorkSteps {
  private responsiveImages: ResponsiveImages;
  private readonly imageSizes = `
 (max-width: 767px) and (min-resolution: 288dpi) 50vw,
 (max-width: 767px) and (min-resolution: 192dpi) 50vw,
 (max-width: 767px) and (min-resolution: 96dpi) 100vw,
  (min-width: 768px) and (max-width: 1279px) 50vw,
  (min-width: 1280px) 33vw,
 100vw`;

  private readonly imageStyles = {
    width: '100%',
    height: 'auto',
    display: 'block',
  };

  private modalComponent: HTMLCmsModalElement;
  private readonly decorativeAlt = '';

  /**
   * set the responsive images for Breakpoints 320, 480, 768,992,1200
   */
  @Prop() images: ResponsiveImages | string;

  /**
   * set the Work Step headline Title
   */
  @Prop() stepTitle: string;

  /**
   * set the Work Step Text
   */
  @Prop() stepText: string;

  /**
   * set the image position like left ore right
   */
  @Prop() imagePosition: 'left' | 'right';

  /**
   * set the background color as token
   */
  @Prop() backgroundColor: string;

  /**
   * set the background color as token
   */
  @Prop() isAlternate: boolean;

  /**
   * set if work step is even or odd
   */
  @Prop() isEven: boolean | undefined;

  /**
   * set the count number of step
   */
  @Prop() stepCounter: string;

  @Element() hostElement: HTMLElement;

  @Watch('images')
  private imagesWatcher(newValue: ResponsiveImages | string) {
    if (newValue) {
      this.responsiveImages =
        typeof newValue === 'string'
          ? (JSON.parse(newValue) as ResponsiveImages)
          : newValue;
    }
  }

  private workStepContainerClass(): CssClassMap {
    return {
      'work-step-container': true,
      [this.imagePosition]: this.imagePosition !== undefined,
    };
  }

  private openModal() {
    if (this.modalComponent && !isMobile()) {
      this.modalComponent.open = true;
    }
  }

  private async modalContent() {
    if (this.modalComponent && !isMobile()) {
      await this.modalComponent.reset();
      this.modalComponent.setModalContent = `<img
        alt='${this.decorativeAlt}'
        src='${this.responsiveImages.bp992}'
        width='992'
        height='661'
        class='work-step-modal-image'
      />`;
    }
  }

  private renderStepImage() {
    return (
      <button
        type='button'
        class={
          this.stepText === undefined
            ? 'image-wrapper only-content'
            : 'image-wrapper'
        }
        onClick={() => {
          this.modalContent();
          this.openModal();
        }}
        onKeyUp={(event) => {
          if (event.key === 'Enter') {
            this.modalContent();
            this.openModal();
          }
        }}
      >
        <div class={'modal-icon'}>
          <cms-icon
            colorToken='--cms-semanticColors-base-weak-iconOnBackground'
            size='md'
            icon='fullscreenTrue'
          />
        </div>
        {this.responsiveImages !== undefined
          ? renderResponsiveImages(
              this.responsiveImages,
              this.imageSizes,
              this.decorativeAlt,
              'work-step-image',
              this.imageStyles,
            )
          : ''}
      </button>
    );
  }

  connectedCallback() {
    if (this.images) {
      this.imagesWatcher(this.images);
    }
  }

  componentDidLoad() {
    this.modalComponent = document.querySelector('cms-modal');
  }

  render() {
    return (
      <Host
        style={
          this.backgroundColor
            ? {
                backgroundColor: `var(${this.backgroundColor})`,
              }
            : null
        }
      >
        <div class={this.workStepContainerClass()}>
          {this.stepText !== undefined ? (
            <div
              class={
                this.images === undefined
                  ? 'text-container only-content'
                  : 'text-container'
              }
            >
              {this.stepTitle === undefined ? null : (
                <div class={'headline-wrapper'}>
                  <span class={'step-count'}>{this.stepCounter}</span>
                  <cms-headline
                    style={{
                      '--cms-theme-text-color':
                        'var(--cms-semanticColors-base-strong-text)',
                      '--cms-spacing-top': '0',
                      '--cms-spacing-bottom': '0',
                      '--cms-spacing-left-right': '0',
                    }}
                    isAdaptive={true}
                    headlineText={this.stepTitle}
                    headlineTag='h3'
                    headlineClass={'step-headline'}
                  />
                </div>
              )}

              <div class={'text-content-wrapper'}>
                <div class={'step-text'} innerHTML={this.stepText} />
              </div>
            </div>
          ) : null}
          {this.responsiveImages !== undefined ? this.renderStepImage() : null}
        </div>
      </Host>
    );
  }
}
